/* eslint-disable */
import { Observable } from 'rxjs';

import {
  CalendarContact,
  CalendarEvent,
  CalendarEventDatetime,
  CalendarEventEditScope,
  CalendarRecurringMutationScope,
  CalendarEventRecurrence,
  CalendarEventReminder,
  CalendarGroup,
  CalendarMeInfo,
  CalendarReservationFilters,
  CalendarSchedule,
  RepetitiveAvailabilityRequest,
  RepetitiveAvailabilityResult,
  RepetitiveEventOptions,
  RepetitiveEventOptionsContext,
  RepetitiveEventsPolicy,
  CalendarUser,
  ExtendedCalendarScheduleItem,
} from '../../models';
import { CalendarServerType } from '../../models/calendar-server-type.enum';

export type CalendarSingleOccurrenceMutationTarget = Pick<CalendarEvent, 'id' | 'type' | 'seriesMasterId'>;

export type CalendarSyncEventIdentityInput = {
  providerKey?: string | null;
  serverType?: string | null;
  serverTypeId?: number | null;
  id?: string | null;
  iCalUId?: string | null;
  seriesMasterId?: string | null;
  occurrenceId?: string | null;
  type?: string | null;
  startUtc?: string | null;
  endUtc?: string | null;
  start?: { dateTime?: string | null; timeZone?: string | null } | null;
  end?: { dateTime?: string | null; timeZone?: string | null } | null;
};

export type CalendarEventLocationInput = {
  roomName: string;
  roomEmail: string;
  roomUsername?: string;
};

export type CalendarEventAttendeeInput = {
  isResource?: boolean;
  address: string;
  name: string;
  type: 'required' | 'optional';
};

export type CalendarEventMutationOptions = {
  isAllDay?: boolean;
};

export interface CalendarsService {
  // optional abstract method
  getCalendarEventsAlternative(
    from: Date,
    to: Date,
    email: string,
    settings?: any
  ): Observable<{ events: any[]; nextLink: string }>;

  supportsCalendarSyncView(): boolean;

  getCalendarSyncEventDiscriminator(event: CalendarSyncEventIdentityInput): string;

  getCalendarSyncDraftReservationShowAs(): { key: string; value: string };

  getSingleOccurrenceMutationTargetEventId(event: CalendarSingleOccurrenceMutationTarget): string;
}

export abstract class CalendarsService {
  protected constructor(public type: CalendarServerType) {}

  abstract getIdentifierFromContact(contact: CalendarContact): string;

  abstract getImageUrl(identifier: string): Observable<string>;

  abstract getMeInfo(): Observable<CalendarMeInfo>;

  abstract availableMeetingProviders(): Observable<string[]>;

  abstract findContact(input: string): Observable<CalendarContact[]>;

  abstract getContactsFromGroup(id: string): Observable<CalendarContact[]>;

  abstract findContactGroup(input: string): Observable<CalendarGroup[]>;

  abstract deleteEvent(id: string, mutationScope?: CalendarRecurringMutationScope): Observable<boolean>;

  supportsCalendarSyncView(): boolean {
    return false;
  }

  getCalendarSyncEventDiscriminator(event: CalendarSyncEventIdentityInput): string {
    const provider = event.providerKey ?? event.serverType ?? event.serverTypeId ?? this.type;
    const primaryId = event.id ?? event.iCalUId ?? event.occurrenceId ?? event.seriesMasterId ?? '';
    const start = event.startUtc ?? event.start?.dateTime ?? '';
    const end = event.endUtc ?? event.end?.dateTime ?? '';

    return [provider, primaryId, start, end].join('|');
  }

  getCalendarSyncDraftReservationShowAs(): { key: string; value: string } {
    return {
      key: 'reservationPopup.free',
      value: 'free',
    };
  }

  getSingleOccurrenceMutationTargetEventId(event: CalendarSingleOccurrenceMutationTarget): string {
    return event.id;
  }

  abstract finishEvent(eventId: string): Observable<{
    id: string;
    start: Date;
    end: Date;
  }>;

  abstract patchEvent(
    visibility: 'public' | 'private',
    showAs: { key: string; value: string },
    eventId: string,
    subject: string,
    text: string,
    start: CalendarEventDatetime,
    end: CalendarEventDatetime,
    locations: CalendarEventLocationInput[],
    attendees: CalendarEventAttendeeInput[],
    onlineMeeting: {
      onlineMeetingProvider: string;
    } | null,
    reminder: CalendarEventReminder,
    recurrence?: CalendarEventRecurrence | null,
    editScope?: CalendarEventEditScope,
    mutationScope?: CalendarRecurringMutationScope,
    options?: CalendarEventMutationOptions
  ): Observable<any>;

  abstract createEvent(
    visibility: 'public' | 'private',
    showAs: { key: string; value: string },
    subject: string,
    text: string,
    start: CalendarEventDatetime,
    end: CalendarEventDatetime,
    locations: CalendarEventLocationInput[],
    attendees: CalendarEventAttendeeInput[],
    onlineMeeting: {
      onlineMeetingProvider: string;
    } | null,
    webexMeeting: {
      body: any;
      url: string;
      code: string;
      password: string;
    } | null,
    reminder: CalendarEventReminder,
    recurrence?: CalendarEventRecurrence | null,
    options?: CalendarEventMutationOptions
  ): Observable<{ id: string; response: string; iCalId: string }>;

  abstract supportsRepetitiveEvents(): boolean;

  abstract supportsAllDayEvents(): boolean;

  abstract getRepetitiveEventsPolicy(): RepetitiveEventsPolicy;

  abstract getRepetitiveEventOptions(context: RepetitiveEventOptionsContext): RepetitiveEventOptions;

  abstract checkRepetitiveAvailability(
    request: RepetitiveAvailabilityRequest
  ): Observable<RepetitiveAvailabilityResult>;

  abstract getCalendarEventById(
    currentUserEmail: string,
    email: string,
    id: string,
    occurrenceId?: string,
    ownerUserId?: string
  ): Observable<CalendarEvent | null>;

  abstract getCalendarEvents(
    currentUserEmail: string,
    from: Date,
    to: Date,
    email: string,
    settings: CalendarReservationFilters | null
  ): Observable<{
    events: CalendarEvent[];
    nextLink: string;
    errorCode: number | null;
    errorDescription: string | null;
  }>;

  abstract getCollegueInfo(identifier: string): Observable<CalendarUser>;

  abstract getScheduleForEmails(
    emails: string[],
    fromDate: Date,
    toDate: Date
  ): Observable<CalendarSchedule<ExtendedCalendarScheduleItem>[] | CalendarSchedule[]>;

  protected getChunks(myArray: any[], chunk_size: number) {
    let index = 0;
    const arrayLength = myArray.length;
    const tempArray: any[] = [];
    let chunk: any[] = [];

    for (index = 0; index < arrayLength; index += chunk_size) {
      chunk = myArray.slice(index, index + chunk_size);
      tempArray.push(chunk);
    }

    return tempArray;
  }

  protected onlyUnique(value: any, index: number, self: any[]) {
    return self.indexOf(value) === index;
  }
}
